import { Schema } from 'prosemirror-model';
import { schema as baseSchema } from 'prosemirror-schema-basic';

const isValidInlineCardUrl = (url: string | null): url is string => {
  if (!url) {
    return false;
  }

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const getInlineCardUrl = (element: HTMLElement): string | null =>
  element.getAttribute('data-inline-card-url') ??
  element.getAttribute('data-smart-link-url') ??
  element.getAttribute('data-smartlink-url') ??
  (element.getAttribute('data-card-appearance') === 'inline'
    ? element.getAttribute('href')
    : null);

const getInlineCardAttrs = (dom: Node) => {
  const url = getInlineCardUrl(dom as HTMLElement);
  return isValidInlineCardUrl(url) ? { url } : false;
};

const isValidLinkHref = (href: string | null): href is string => {
  if (href === '') {
    return true;
  }

  if (href === null) {
    return false;
  }

  try {
    const parsed = new URL(href, 'https://example.com');
    return ['http:', 'https:', 'mailto:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

/**
 * Creates an enhanced ProseMirror schema with proper underline and strike marks
 */
export function createEnhancedSchema(): Schema {
  const nodes = baseSchema.spec.nodes
    // We can't use `addListNodes` from prosemirror-schema-list as it defines the
    // node keys as `bullet_list`, `ordered_list` and `list_item`, whereas ADF
    // defines them as `bulletList`, `orderedList` and `listItem`
    .addToEnd('bulletList', {
      content: 'listItem+',
      group: 'block',
      parseDOM: [{ tag: 'ul' }],
      toDOM(): [string, number] {
        return ['ul', 0];
      },
    })
    .addToEnd('orderedList', {
      content: 'listItem+',
      group: 'block',
      parseDOM: [{ tag: 'ol' }],
      toDOM(): [string, number] {
        return ['ol', 0];
      },
    })
    .addToEnd('listItem', {
      content: 'paragraph block*',
      parseDOM: [{ tag: 'li' }],
      toDOM(): [string, number] {
        return ['li', 0];
      },
    })
    // This is needed to support Atlassian platform emojis that get rendered as
    // images, e.g. `:smile:`. In ADF, these get split out to their own `<span>`
    // elements instead of being part of the text node like native Unicode emojis.
    .addToEnd('emoji', {
      inline: true,
      group: 'inline',
      selectable: false,
      atom: true,
      attrs: {
        shortName: { default: null },
        text: { default: null },
      },
      parseDOM: [
        {
          tag: 'span[data-emoji-text]',
          getAttrs: (dom) => {
            const element = dom as HTMLElement;
            return {
              shortName: element.getAttribute('data-emoji-short-name'),
              text: element.getAttribute('data-emoji-text'),
            };
          },
        },
      ],
      toDOM: (element) => {
        return [
          'span',
          {
            ['data-emoji-short-name']: element.attrs.shortName,
            ['data-emoji-text']: element.attrs.text,
          },
          element.attrs.text || '',
        ];
      },
    })
    // Support for mention nodes in ADF content following the specification
    // https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/mention/
    .addToEnd('mention', {
      inline: true,
      group: 'inline',
      selectable: false,
      atom: true,
      attrs: {
        id: { default: null },
        text: { default: null },
        accessLevel: { default: null },
        userType: { default: null },
      },
      parseDOM: [
        {
          tag: 'span[data-mention-id]',
          getAttrs: (dom) => {
            const element = dom as HTMLElement;
            return {
              id: element.getAttribute('data-mention-id'),
              text: element.textContent,
              accessLevel: element.getAttribute('data-access-level'),
              userType: element.getAttribute('data-user-type'),
            };
          },
        },
      ],
      toDOM: (node) => {
        return [
          'span',
          {
            ['data-mention-id']: node.attrs.id,
            ['data-access-level']: node.attrs.accessLevel,
            ['data-user-type']: node.attrs.userType,
            class: 'mention',
          },
          node.attrs.text || '',
        ];
      },
    })
    .addToEnd('inlineCard', {
      inline: true,
      group: 'inline',
      selectable: false,
      atom: true,
      attrs: {
        url: { default: null },
      },
      parseDOM: [
        {
          tag: 'a[data-inline-card-url]',
          getAttrs: getInlineCardAttrs,
        },
        {
          tag: 'a[data-smart-link-url]',
          getAttrs: getInlineCardAttrs,
        },
        {
          tag: 'a[data-smartlink-url]',
          getAttrs: getInlineCardAttrs,
        },
        {
          tag: 'a[data-card-appearance="inline"]',
          getAttrs: getInlineCardAttrs,
        },
      ],
      toDOM: (node) => {
        const url = node.attrs.url ?? '';
        return ['a', { href: url, 'data-inline-card-url': url }, url];
      },
    })
    // The default ProseMirror schema's hard break node is defined as
    // `hard_break`, in contrast to ADF's `hardBreak`. Since we are building on
    // top of the default schema, we must replace that key entry entirely to
    // avoid a conflict.
    .update(
      'hard_break',
      {
        inline: true,
        group: 'inline',
        selectable: false,
        atom: true,
        parseDOM: [{ tag: 'br' }],
        toDOM(): [string] {
          return ['br'];
        },
      },
      'hardBreak'
    );

  const marks = baseSchema.spec.marks
    .addToEnd('underline', {
      parseDOM: [
        { tag: 'u' },
        {
          style: 'text-decoration',
          getAttrs: (value: HTMLElement | string): false | null =>
            value === 'underline' ? null : false,
        },
      ],
      toDOM(): [string, number] {
        return ['u', 0];
      },
    })
    .addToEnd('strike', {
      parseDOM: [
        { tag: 'strike' },
        { tag: 's' },
        {
          style: 'text-decoration',
          getAttrs: (value: HTMLElement | string): false | null =>
            value === 'line-through' ? null : false,
        },
      ],
      toDOM(): [string, number] {
        return ['s', 0];
      },
    })
    .addToEnd('link', {
      attrs: { href: { default: null } },
      inclusive: false,
      parseDOM: [
        {
          tag: 'a[href]',
          getAttrs: (dom) => {
            const element = dom as HTMLElement;
            if (isValidInlineCardUrl(getInlineCardUrl(element))) {
              return false;
            }
            const href = element.getAttribute('href');
            return isValidLinkHref(href) ? { href } : false;
          },
        },
      ],
      toDOM(mark) {
        return ['a', { href: mark.attrs.href }, 0];
      },
    })
    .addToEnd('textColor', {
      attrs: { color: { default: null } },
      parseDOM: [
        {
          style: 'color',
          getAttrs: (value: HTMLElement | string) =>
            typeof value === 'string'
              ? { color: value }
              : { color: (value as HTMLElement).style?.color || null },
        },
        // Support Confluence/Atlaskit editor text color marks which use a
        // data attribute instead of an inline style.
        {
          tag: 'span[data-text-custom-color]',
          getAttrs: (dom) => {
            const el = dom as HTMLElement;
            return { color: el.getAttribute('data-text-custom-color') };
          },
        },
      ],
      toDOM(mark) {
        const color = mark.attrs?.color;
        return color
          ? ['span', { style: `color: ${color}`, 'data-text-custom-color': color }, 0]
          : ['span', 0];
      },
    })
    .addToEnd('subsup', {
      attrs: { type: { default: 'sub' } },
      parseDOM: [
        { tag: 'sub', getAttrs: () => ({ type: 'sub' }) },
        { tag: 'sup', getAttrs: () => ({ type: 'sup' }) },
      ],
      toDOM(mark) {
        const type = mark.attrs?.type;
        const tag = type === 'sup' ? 'sup' : 'sub';
        return [tag, 0];
      },
    })
    .addToEnd('code', {
      parseDOM: [{ tag: 'code', preserveWhitespace: true }],
      toDOM(): [string, number] {
        return ['code', 0];
      },
    })
    .addToEnd('backgroundColor', {
      attrs: { color: { default: null } },
      parseDOM: [
        {
          style: 'background-color',
          getAttrs: (value: HTMLElement | string) =>
            typeof value === 'string'
              ? { color: value }
              : { color: (value as HTMLElement).style?.backgroundColor || null },
        },
        // Support Confluence/Atlaskit editor background color marks which use
        // a data attribute instead of an inline style.
        {
          tag: 'span[data-background-custom-color]',
          getAttrs: (dom) => {
            const el = dom as HTMLElement;
            return { color: el.getAttribute('data-background-custom-color') };
          },
        },
      ],
      toDOM(mark) {
        const color = mark.attrs?.color;
        return color
          ? [
              'span',
              {
                style: `background-color: ${color}`,
                'data-background-custom-color': color,
              },
              0,
            ]
          : ['span', 0];
      },
    });

  return new Schema({ nodes, marks });
}
